import { logger } from '../../utils/logger';
import type { SnapshotComparisonResult } from '../../types/snapshotComparison';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

const buildComparisonErrorMessage = async (response: Response): Promise<string> => {
  const fallback = `Failed to fetch snapshot comparison (${response.status})`;

  try {
    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';

    if (
      contentType.includes('application/json') ||
      contentType.includes('application/problem+json')
    ) {
      const body = (await response.json()) as Record<string, unknown>;
      const candidate = [body['error'], body['detail'], body['title'], body['message']].find(
        (v) => typeof v === 'string' && v.trim().length > 0
      ) as string | undefined;

      if (candidate) {
        return candidate;
      }
    }

    const text = await response.text();
    if (text.trim().length > 0) {
      return text;
    }
  } catch {
    // Fall through to fallback message.
  }

  return response.statusText?.trim().length ? `${fallback}: ${response.statusText}` : fallback;
};

/**
 * Fetch snapshot comparison results for a given migration from the backend API.
 * Calls GET /api/Migrations/{id}/compare and returns a diff of Genesys Cloud
 * resources that changed between two snapshots of the target organisation.
 *
 * @param bearerToken - Authentication token
 * @param migrationId - ID of the migration to compare
 * @param snapshot1 - Optional custom path to the first (before) snapshot. Defaults to {migrationId}/targetOrgSnapshot-preImport
 * @param snapshot2 - Optional custom path to the second (after) snapshot. Defaults to {migrationId}/targetOrgSnapshot-postImport
 * @param suppressDeletions - When true, omit resources present in the before snapshot but missing from the after snapshot. A forward migration never deletes target-org resources, so such entries are export-capture artifacts. Used by the rollback impact preview.
 */
export const fetchSnapshotComparison = async (
  bearerToken: string,
  migrationId: string,
  snapshot1?: string,
  snapshot2?: string,
  suppressDeletions = false
): Promise<SnapshotComparisonResult> => {
  const baseUrl = getEnv().VITE_EDCI_API_URL;

  const snap1 = snapshot1 ?? `${migrationId}/targetOrgSnapshot-preImport`;
  const snap2 = snapshot2 ?? `${migrationId}/targetOrgSnapshot-postImport`;

  const params = new URLSearchParams();
  params.set('snapshot1', snap1);
  params.set('snapshot2', snap2);
  if (suppressDeletions) {
    params.set('suppressDeletions', 'true');
  }
  const query = `?${params.toString()}`;
  const url = `${baseUrl}/api/Migrations/${encodeURIComponent(migrationId)}/compare${query}`;

  logger.info('Fetching snapshot comparison', {
    migrationId,
    snapshot1: snap1,
    snapshot2: snap2
  });

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: getV2AuthHeaders(bearerToken)
  });

  if (!response.ok) {
    const message = await buildComparisonErrorMessage(response);
    logger.error('Failed to fetch snapshot comparison', {
      migrationId,
      status: response.status,
      message
    });
    throw new Error(message);
  }

  const data = (await response.json()) as SnapshotComparisonResult;
  logger.info('Snapshot comparison fetched', {
    migrationId,
    resourceTypes: data.resourceTypes.length
  });
  return data;
};
